import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { useAuth } from '../context/AuthContext';
import './STOList.css';

const STOList = () => {
  const navigate = useNavigate();
  const { fetchWithAuth } = useAuth();
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadFiles = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth('/api/sto/files');
      if (!response.ok) {
        throw new Error('Failed to load files');
      }
      const data = await response.json();
      // Sort by upload date descending
      data.sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
      setFiles(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const loadFileDetails = async (fileId) => {
    try {
      setIsLoading(true);
      const response = await fetchWithAuth(`/api/sto/file/${fileId}`);
      if (!response.ok) {
        throw new Error('Failed to load file details');
      }
      const data = await response.json();
      setSelectedFile(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileClick = (fileId) => {
    loadFileDetails(fileId);
  };

  const handleBack = () => {
    setSelectedFile(null);
    setError('');
  };

  const handleDelete = async (eOrFileId, fileIdParam) => {
    const fileId = fileIdParam != null ? fileIdParam : eOrFileId;
    if (eOrFileId && typeof eOrFileId.stopPropagation === 'function') eOrFileId.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this file and all its consignments?')) {
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/sto/file/${fileId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete file');
      }

      if (selectedFile && selectedFile.id === fileId) {
        setSelectedFile(null);
      }
      setError('');
      loadFiles();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#F59E0B',
      receiving: '#3B82F6',
      completed: '#10B981',
    };
    return colors[status] || '#6B7280';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'Pending',
      receiving: 'Receiving',
      completed: 'Completed',
    };
    return texts[status] || 'Unknown';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  if (isLoading && !selectedFile) {
    return (
      <Layout>
        <div className="sto-list-container">
          <div className="header">
            <button className="back-button" onClick={() => navigate('/')}>
              ← Back
            </button>
            <h1>STO Files</h1>
          </div>
          <div className="loading">Loading...</div>
        </div>
      </Layout>
    );
  }

  // Show file details view
  if (selectedFile) {
    return (
      <Layout>
        <div className="sto-list-container">
          <div className="header">
            <button className="back-button" onClick={handleBack}>
              ← Back to Files
            </button>
            <h1>{selectedFile.fileName}</h1>
            <button className="refresh-button" onClick={() => loadFileDetails(selectedFile.id)}>
              🔄 Refresh
            </button>
          </div>

        <div className="content">
          <div className="file-info-header">
            <div className="file-info-item">
              <span className="file-info-label">Uploaded:</span>
              <span className="file-info-value">
                {new Date(selectedFile.uploadedAt).toLocaleString()}
              </span>
            </div>
            <div className="file-info-item">
              <span className="file-info-label">Size:</span>
              <span className="file-info-value">{formatFileSize(selectedFile.fileSize)}</span>
            </div>
            <div className="file-info-item">
              <span className="file-info-label">Consignments:</span>
              <span className="file-info-value">{selectedFile.consignments.length}</span>
            </div>
            <button
              className="delete-file-button"
              onClick={() => handleDelete(selectedFile.id)}
            >
              🗑️ Delete File
            </button>
          </div>

          {error && <div className="error-message">{error}</div>}

          {selectedFile.consignments.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h2>No Consignments</h2>
              <p>This file contains no consignments</p>
            </div>
          ) : (
            <div className="consignments-grid">
              {selectedFile.consignments.map((consignment) => (
                <div key={consignment.id} className="consignment-card">
                  <div className="consignment-header">
                    <h3>{consignment.documentHeaderText}</h3>
                    <div
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(consignment.status) }}
                    >
                      {getStatusText(consignment.status)}
                    </div>
                  </div>
                  <div className="consignment-info">
                    <div className="info-item">
                      <span className="info-label">ID:</span>
                      <span className="info-value">{consignment.id}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Items:</span>
                      <span className="info-value">{consignment.items.length}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Posting Date:</span>
                      <span className="info-value">
                        {consignment.postingDate
                          ? new Date(consignment.postingDate).toLocaleDateString()
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Created:</span>
                      <span className="info-value">
                        {new Date(consignment.createdAt).toLocaleString()}
                      </span>
                    </div>
                    {consignment.updatedAt && (
                      <div className="info-item">
                        <span className="info-label">Last Updated:</span>
                        <span className="info-value">
                          {new Date(consignment.updatedAt).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="consignment-items">
                    <h4>Items Preview:</h4>
                    <div className="items-list">
                      {consignment.items.slice(0, 3).map((item, idx) => (
                        <div key={idx} className="item-preview">
                          <span className="item-material">{item.material}</span>
                          <span className="item-qty">Qty: {item.quantity}</span>
                        </div>
                      ))}
                      {consignment.items.length > 3 && (
                        <div className="more-items">
                          +{consignment.items.length - 3} more items
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </Layout>
    );
  }

  // Show files list view
  return (
    <Layout>
      <div className="sto-list-container">
        <div className="header">
          <button className="back-button" onClick={() => navigate('/')}>
            ← Back to Main Menu
          </button>
          <h1>STO Files</h1>
          <button className="refresh-button" onClick={loadFiles}>
            🔄 Refresh
          </button>
        </div>

      <div className="content">
        {error && <div className="error-message">{error}</div>}

        {files.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h2>No STO Files</h2>
            <p>Import STO files to get started</p>
            <button
              className="import-button"
              onClick={() => navigate('/sto-import')}
            >
              Import STO
            </button>
          </div>
        ) : (
          <div className="files-list">
            {files.map((file) => (
              <div
                key={file.id}
                className="file-card"
                onClick={() => handleFileClick(file.id)}
              >
                <div className="file-card-header">
                  <div className="file-icon">📄</div>
                  <div className="file-details">
                    <h3 className="file-name">{file.fileName}</h3>
                    <div className="file-meta">
                      <span className="file-date">
                        {new Date(file.uploadedAt).toLocaleString()}
                      </span>
                      <span className="file-size">{formatFileSize(file.fileSize)}</span>
                      {file.consignment_location_name && (
                        <span className="file-location" title="Consignment location">
                          📍 {file.consignment_location_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="file-card-actions">
                    <button
                      type="button"
                      className="file-delete-btn"
                      onClick={(e) => handleDelete(e, file.id)}
                      title="Delete this file"
                      aria-label="Delete file"
                    >
                      🗑 Delete
                    </button>
                    <span className="file-arrow">→</span>
                  </div>
                </div>
                <div className="file-stats">
                  <div className="stat-item">
                    <span className="stat-label">Consignments:</span>
                    <span className="stat-value">{file.consignmentCount}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Total Items:</span>
                    <span className="stat-value">{file.totalItems}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Pending:</span>
                    <span className="stat-value" style={{ color: '#F59E0B' }}>
                      {file.statusSummary.pending}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Receiving:</span>
                    <span className="stat-value" style={{ color: '#3B82F6' }}>
                      {file.statusSummary.receiving}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Completed:</span>
                    <span className="stat-value" style={{ color: '#10B981' }}>
                      {file.statusSummary.completed}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </Layout>
  );
};

export default STOList;
